import React from 'react';
import { addDays } from 'date-fns';
import { createDateFilter, DATE_REGEX, getLineGraphLegend, isWeekOrMonth } from './performanceTabUtil';
import { METRIC_TO_GRAPH_TYPE, MONTH_NAMES, PeriodType, WEEKDAY_NAMES } from './performanceConstants';
import { DashboardContext } from './revenueUpliftContexts';
import { MONTH, PERIOD_SCOPE, QUARTER, WEEK, YEAR, getRanges, utcDate } from './relativeDates';
import { DateType } from '../../../util/format';

const relativeToday = relativeDates => {
  const today = utcDate(
    `${relativeDates.yesterday.getFullYear()}-${relativeDates.yesterday.getMonth() +
      1}-${relativeDates.yesterday.getDate()}`,
  );

  today.setDate(today.getDate() + 1);

  return today;
};

/**
 * Accumulates values by date and type extracted from the key.
 * The key must follow the format "type_YYYY-M" or "type_YYYY-MM" for monthly data,
 * or "type_YYYY-M-D" or "type_YYYY-MM-DD" for daily data.
 * An optional dateFilter function can be provided to exclude certain dates.
 *
 * @param {Array<Object>} data - Array of objects with keys in format type_date
 * @param {Function} [dateFilter=() => true] - A predicate function that receives a date string
 * and returns true if it should be included.
 * @returns {Object} Object with the shape { "date": { type1: total, type2: total, ... } }
 */
const accumulateByDateAndType = (data, dateFilter = () => true) =>
  data.reduce((acc, item) => {
    Object.entries(item).forEach(([key, value]) => {
      const match = key.match(DATE_REGEX);
      if (match) {
        const [, type, date] = match;
        if (!dateFilter(date)) {
          return;
        }
        if (!acc[date]) {
          acc[date] = {};
        }
        if (typeof value === 'number') {
          acc[date][type] = (acc[date][type] || 0) + value;
        }
      }
    });
    return acc;
  }, {});

/**
 * Accepts a string in YYYY-MM, YYYY-M, YYYY-MM-DD, YYYY-M-D format or a Date object
 *
 * Note that day zero of the next month === last day of this date's month
 *
 * Also, it is OK to use Date() here since it will be using a utcDate() to start
 */
const lastDateOfMonth = date => {
  let thisDate = date;

  if (typeof date === 'string' || date instanceof String) {
    thisDate = utcDate(date);
  }

  return new Date(thisDate.getFullYear(), thisDate.getMonth() + 1, 0);
};

const determineWeekRange = (accumulated, relativeDates) => {
  const {
    weekStart,
    weekEnd,
    weekLastStart,
    weekLastEnd,
    weekPriorStart,
    weekPriorEnd,
    stlyWeekStart,
    stlyWeekEnd,
    stlyWeekLastStart,
    stlyWeekLastEnd,
  } = relativeDates;

  const accumulatedDates = Object.keys(accumulated)
    .filter(key => key.split('-').length === 3)
    .map(key => utcDate(key));

  if (accumulatedDates.some(date => date >= weekStart && date <= weekEnd)) {
    return { start: weekStart, end: weekEnd };
  }

  if (accumulatedDates.some(date => date >= weekLastStart && date <= weekLastEnd)) {
    return { start: weekLastStart, end: weekLastEnd };
  }

  if (accumulatedDates.some(date => date >= weekPriorStart && date <= weekPriorEnd)) {
    return { start: weekPriorStart, end: weekPriorEnd };
  }

  if (accumulatedDates.some(date => date >= stlyWeekStart && date <= stlyWeekEnd)) {
    return { start: stlyWeekStart, end: stlyWeekEnd };
  }

  if (accumulatedDates.some(date => date >= stlyWeekLastStart && date <= stlyWeekLastEnd)) {
    return { start: stlyWeekLastStart, end: stlyWeekLastEnd };
  }

  return { start: weekStart, end: weekEnd };
};

const determineQuarterRange = (accumulated, relativeDates) => {
  const {
    quarterStart,
    quarterEnd,
    quarterLastStart,
    quarterLastEnd,
    quarterPriorStart,
    quarterPriorEnd,
    stlyQuarterStart,
    stlyQuarterEnd,
    stlyQuarterLastStart,
    stlyQuarterLastEnd,
  } = relativeDates;

  const accumulatedDates = Object.keys(accumulated)
    .filter(key => key.split('-').length === 2)
    .map(key => utcDate(key));

  if (accumulatedDates.some(date => date >= quarterStart && date <= quarterEnd)) {
    return { start: quarterStart, end: quarterEnd };
  }

  if (accumulatedDates.some(date => date >= quarterLastStart && date <= quarterLastEnd)) {
    return { start: quarterLastStart, end: quarterLastEnd };
  }

  if (accumulatedDates.some(date => date >= quarterPriorStart && date <= quarterPriorEnd)) {
    return { start: quarterPriorStart, end: quarterPriorEnd };
  }

  if (accumulatedDates.some(date => date >= stlyQuarterStart && date <= stlyQuarterEnd)) {
    return { start: stlyQuarterStart, end: stlyQuarterEnd };
  }

  if (accumulatedDates.some(date => date >= stlyQuarterLastStart && date <= stlyQuarterLastEnd)) {
    return { start: stlyQuarterLastStart, end: stlyQuarterLastEnd };
  }

  return { start: quarterStart, end: quarterEnd };
};

/**
 * Converts accumulated daily data for a WEEK period into series.
 * Aggregates amounts by weekday (Monday to Sunday) for dates within the week determined
 * from the minimum accumulated key date.
 *
 * @param {Object} accumulated - The accumulated data with keys in "YYYY-M-D" format.
 * @param {Object} relativeDates - The relative dates object.
 * @returns {Array} Array of series objects.
 */
const convertDaySeriesForWeek = (accumulated, relativeDates) => {
  const { start: weekStart, end: weekEnd } = determineWeekRange(accumulated, relativeDates);

  // Aggregate amounts by type for dates within that week.
  const aggregatedByType = {};
  Object.entries(accumulated).forEach(([key, value]) => {
    const forecastDate = utcDate(key);
    if (forecastDate >= weekStart && forecastDate <= weekEnd) {
      Object.entries(value).forEach(([type, amount]) => {
        if (!aggregatedByType[type]) {
          aggregatedByType[type] = Array(7).fill(0);
        }
        // Adjust getDay so Monday=0, Sunday=6.
        const weekdayIndex = (forecastDate.getDay() + 6) % 7;
        aggregatedByType[type][weekdayIndex] += amount;
      });
    }
  });

  // Build series per type using weekday names as x values.
  const series = [];
  Object.entries(aggregatedByType).forEach(([type, weekdayData]) => {
    const data = [];
    for (let i = 0; i < 7; i++) {
      const currentDayDate = addDays(weekStart, i);
      data.push({
        x: WEEKDAY_NAMES[i],
        y: weekdayData[i] || 0,
        dashed: currentDayDate >= relativeDates.yesterday,
        date: currentDayDate,
      });
    }
    series.push({ type, data });
  });
  return series;
};

/**
 * Converts accumulated daily data (non-WEEK period, e.g. MONTH) into series.
 * Groups data by year-month and then builds a series per type with 31 day values.
 *
 * @param {Object} accumulated - The accumulated data with keys in "YYYY-M-D" format.
 * @param {Object} relativeDates - The relative dates object.
 * @returns {Array} Array of series objects.
 */
const convertDaySeriesForNonWeek = (accumulated, relativeDates) => {
  const groups = {};
  Object.entries(accumulated).forEach(([key, value]) => {
    const parts = key.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      const groupKey = `${year}-${parseInt(month, 10)}`;
      if (!groups[groupKey]) {
        groups[groupKey] = {};
      }
      Object.entries(value).forEach(([type, amount]) => {
        if (!groups[groupKey][type]) {
          groups[groupKey][type] = {};
        }
        groups[groupKey][type][parseInt(day, 10)] = amount;
      });
    }
  });

  const series = [];
  Object.entries(groups).forEach(([groupKey, typesObj]) => {
    const lastDay = lastDateOfMonth(groupKey).getDate();
    Object.entries(typesObj).forEach(([type, dayData]) => {
      const data = [];
      for (let day = 1; day <= lastDay; day++) {
        const fullDate = utcDate(`${groupKey}-${day}`);
        data.push({
          x: day,
          y: dayData[day] || 0,
          dashed: fullDate >= relativeDates.yesterday,
          date: fullDate,
        });
      }
      series.push({ type, data });
    });
  });
  return series;
};

const getLineChartQuarterLabels = (relativeDates, periodScope) => {
  const { quarterStart, quarterLastStart } = relativeDates;
  let currentQuarterStart = quarterStart;

  switch (periodScope) {
    case PERIOD_SCOPE.CURRENT:
      currentQuarterStart = quarterStart;
      break;
    case PERIOD_SCOPE.PREVIOUS:
      currentQuarterStart = quarterLastStart;
      break;
    default:
      currentQuarterStart = quarterStart;
  }

  const startMonth = currentQuarterStart.getMonth() + 1; // 1–12
  const quarterMonths = [startMonth, startMonth + 1, startMonth + 2];

  return quarterMonths.map(m => MONTH_NAMES[m - 1]);
};

/**
 * Converts accumulated monthly data for a QUARTER period into series.
 *
 * @param {Object} accumulated - The accumulated data with keys in "YYYY-M" format.
 * @param {Object} relativeDates - The relative dates object.
 * @param {string} periodScope - The scope of the period (e.g., CURRENT, PREVIOUS).
 * @returns {Array} Array of series objects.
 */
const convertMonthSeriesForQuarter = (accumulated, relativeDates, periodScope) => {
  const series = [];
  const { start: quarterDataStart } = determineQuarterRange(accumulated, relativeDates);
  const currentYear = quarterDataStart.getFullYear();
  const startMonth = quarterDataStart.getMonth() + 1; // 1–12
  const quarterMonths = [startMonth, startMonth + 1, startMonth + 2];

  // Get the quarter labels for the x-axis depending on the period scope
  const quarterXLabels = getLineChartQuarterLabels(relativeDates, periodScope);

  // Aggregate data for quarter
  const aggregated = {};
  Object.entries(accumulated).forEach(([key, value]) => {
    const parts = key.split('-');
    if (parts.length === 2) {
      const [, monthStr] = parts;
      const month = parseInt(monthStr, 10);
      Object.entries(value).forEach(([type, amount]) => {
        if (!aggregated[type]) {
          aggregated[type] = {};
        }
        aggregated[type][month] = (aggregated[type][month] || 0) + amount;
      });
    }
  });

  // Build series for quarter by remapping each original month to quarterXLabels.
  Object.entries(aggregated).forEach(([type, monthData]) => {
    const data = [];
    quarterMonths.forEach((origMonth, idx) => {
      const fullDate = utcDate(`${currentYear}-${origMonth}`);
      const sameYear = fullDate.getFullYear() === relativeDates.yesterday.getFullYear();
      data.push({
        x: quarterXLabels[idx],
        y: monthData[origMonth] || 0,
        dashed: sameYear
          ? fullDate.getMonth() >= relativeDates.yesterday.getMonth() - 1 // -1 because current month is not complete yet
          : fullDate.getFullYear() > relativeDates.yesterday.getFullYear(),
        date: fullDate,
      });
    });
    series.push({ type, data });
  });

  return series;
};

/**
 * Converts accumulated monthly data for non-QUARTER periods (e.g. YEAR) into series.
 * It groups data by year and then builds a series per type with 12 month values.
 *
 * @param {Object} accumulated - The accumulated data with keys in "YYYY-M" format.
 * @param {Object} relativeDates - The relative dates object.
 * @returns {Array} Array of series objects.
 */
const convertMonthSeriesForNonQuarter = (accumulated, relativeDates) => {
  const groups = {};
  Object.entries(accumulated).forEach(([key, value]) => {
    const parts = key.split('-');
    if (parts.length === 2) {
      const [year, month] = parts;
      if (!groups[year]) {
        groups[year] = {};
      }
      Object.entries(value).forEach(([type, amount]) => {
        if (!groups[year][type]) {
          groups[year][type] = {};
        }
        groups[year][type][parseInt(month, 10)] = amount;
      });
    }
  });
  const series = [];
  Object.entries(groups).forEach(([year, typesObj]) => {
    Object.entries(typesObj).forEach(([type, monthData]) => {
      const data = [];
      for (let m = 1; m <= 12; m++) {
        const fullDate = utcDate(`${year}-${m}`);
        const sameYear = fullDate.getFullYear() === relativeDates.yesterday.getFullYear();
        data.push({
          x: MONTH_NAMES[m - 1],
          y: monthData[m] || 0,
          dashed: sameYear
            ? fullDate.getMonth() >= relativeDates.yesterday.getMonth() - 1 // -1 because current month is not complete yet
            : fullDate.getFullYear() > relativeDates.yesterday.getFullYear(),
          date: fullDate,
        });
      }
      series.push({ legend: year, type, data });
    });
  });
  return series;
};

/**
 * Main function to convert accumulated data into series for graphing.
 * It delegates the conversion to smaller helper functions based on granularity and period.
 *
 * @param {Object} accumulated - The accumulated data.
 * @param {string} granularity - "day" or "month".
 * @param {string} graphType - The type (e.g., "revenue" or "demand") to filter on.
 * @param {string} period - The period (e.g., WEEK, QUARTER, etc.).
 * @param {Object} relativeDates - The relative dates object.
 * @returns {Array} Filtered series array (only series of the specified graphType).
 */
const convertAccumulatedToSeries = (accumulated, granularity, graphType, period, relativeDates, periodScope) => {
  let seriesArray = [];
  if (granularity === 'day') {
    seriesArray =
      period === WEEK
        ? convertDaySeriesForWeek(accumulated, relativeDates)
        : convertDaySeriesForNonWeek(accumulated, relativeDates);
  } else if (granularity === 'month') {
    seriesArray =
      period === QUARTER
        ? convertMonthSeriesForQuarter(accumulated, relativeDates, periodScope)
        : convertMonthSeriesForNonQuarter(accumulated, relativeDates);
  }

  // Fallback: if no series were generated, return a default series with zero values.
  if (seriesArray.length === 0) {
    let data = [];
    if (granularity === 'day') {
      data =
        period === WEEK
          ? Array.from({ length: 7 }, (_, i) => ({ x: WEEKDAY_NAMES[i], y: 0 }))
          : Array.from({ length: 31 }, (_, i) => ({ x: i + 1, y: 0 }));
    } else if (granularity === 'month') {
      data = Array.from({ length: 12 }, (_, i) => ({ x: MONTH_NAMES[i], y: 0 }));
    }
    seriesArray.push({ type: graphType, data });
  }
  // Filter the series to only return those matching the specified graphType.
  return seriesArray.filter(series => series.type.includes(graphType));
};

const combineAccumulatedData = (accumulatedHistoryData, accumulatedForecastData, operation) => {
  const combinedAccumulatedData = { ...accumulatedHistoryData, ...accumulatedForecastData };

  Object.keys(combinedAccumulatedData).forEach(key => {
    if (
      Object.prototype.hasOwnProperty.call(accumulatedHistoryData, key) &&
      Object.prototype.hasOwnProperty.call(accumulatedForecastData, key)
    ) {
      combinedAccumulatedData[key] = operation(accumulatedHistoryData[key], accumulatedForecastData[key]);
    }
  });

  return combinedAccumulatedData;
};

const combine = (history, forecast) => {
  const combinedData = {};

  Object.entries(forecast).forEach(([key, value]) => {
    combinedData[key] = value + (history[key] || 0);
    combinedData[`${key}_forecast`] = value;
  });

  return combinedData;
};

const getLineGraphData = (
  period,
  metric,
  dailyHistory,
  dailyForecast,
  monthlyHistory,
  monthlyForecast,
  rangesApi,
  relativeDates,
  dateFormatter,
) => {
  const { granularity, currentRange, lastRange, stlyRange, periodScope } = rangesApi;

  // For current period, combine forecast and history; for others, use history only
  const currentHistoryData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;
  const currentForecastData = isWeekOrMonth(period) ? dailyForecast : monthlyForecast;
  const lastData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;
  const stlyData = isWeekOrMonth(period) ? dailyHistory : monthlyHistory;

  const currentHistoryAccumulated = accumulateByDateAndType(
    currentHistoryData,
    createDateFilter(currentRange.start, currentRange.end),
  );
  const currentForecastAccumulated = accumulateByDateAndType(
    currentForecastData,
    createDateFilter(currentRange.start, currentRange.end),
  );
  const currentAccumulated = combineAccumulatedData(currentHistoryAccumulated, currentForecastAccumulated, combine);

  const lastAccumulated = accumulateByDateAndType(lastData, createDateFilter(lastRange.start, lastRange.end));
  const stlyAccumulated = stlyRange
    ? accumulateByDateAndType(stlyData, createDateFilter(stlyRange.start, stlyRange.end))
    : {};

  const graphType = METRIC_TO_GRAPH_TYPE[metric];

  const currentSeries = convertAccumulatedToSeries(
    currentAccumulated,
    granularity,
    graphType,
    period,
    relativeDates,
    periodScope,
  );
  const lastSeries = convertAccumulatedToSeries(
    lastAccumulated,
    granularity,
    graphType,
    period,
    relativeDates,
    periodScope,
  );
  const stlySeries = stlyRange
    ? convertAccumulatedToSeries(stlyAccumulated, granularity, graphType, period, relativeDates, periodScope)
    : [];

  const finalSeries = [];
  if (currentSeries.length > 0) {
    finalSeries.push({
      periodType: PeriodType.CURRENT,
      ...currentSeries[0],
      legend: getLineGraphLegend(PeriodType.CURRENT, period, rangesApi, dateFormatter),
    });
  }

  // This is a bit of a kludge...
  //
  // For monthly data there could be an overlap of history and forecast.
  // If so, then the other series will contain 1 month of non-zero "y" value which represents the portion of
  // the month that is forecast. Note that the currentSeries[0] "y" value is history and forecast combined.
  // This extra attribute will be stored in the overlapping month as "y_forecast" and will be used in
  // getBarChartValues()
  const forecastData =
    currentSeries.length > 1 && currentSeries[1].type.includes('_forecast') ? currentSeries[1].data : null;
  if (forecastData) {
    Object.entries(forecastData).forEach(([key, value]) => {
      if (value.y > 0) {
        currentSeries[0].data[key].y_forecast = value.y;
      }
    });
  }
  // End of kludge

  if (lastSeries.length > 0) {
    finalSeries.push({
      periodType: PeriodType.LAST,
      ...lastSeries[0],
      legend: getLineGraphLegend(PeriodType.LAST, period, rangesApi, dateFormatter),
    });
  }

  if (stlySeries.length > 0 && period !== YEAR) {
    finalSeries.push({
      periodType: PeriodType.STLY,
      ...stlySeries[0],
      legend: getLineGraphLegend(PeriodType.STLY, period, rangesApi, dateFormatter),
    });
  }

  return finalSeries;
};

const getBarLabels = (periodType, period, barChartDate, rangesApi, dateFormatter) => {
  const emptyLabels = { cumulativeBarLabel: '', barLabel: '' };

  const { currentRange, lastRange, stlyRange } = rangesApi;

  switch (periodType) {
    case PeriodType.CURRENT:
      switch (period) {
        case WEEK:
        case MONTH: {
          const cumulativeBarLabel = dateFormatter(currentRange.start, { endDate: barChartDate });
          const barLabel = dateFormatter(barChartDate, { type: DateType.DAY_YEAR });
          return { cumulativeBarLabel, barLabel };
        }
        case QUARTER:
        case YEAR: {
          const endDate = lastDateOfMonth(barChartDate);
          const cumulativeBarLabel = dateFormatter(currentRange.start, { endDate });
          const barLabel = dateFormatter(barChartDate, { endDate });
          return { cumulativeBarLabel, barLabel };
        }
        default:
          return emptyLabels;
      }
    case PeriodType.LAST:
      switch (period) {
        case WEEK: {
          const sameDayLastWeek = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          sameDayLastWeek.setDate(sameDayLastWeek.getDate() - 7);

          const cumulativeBarLabel = dateFormatter(lastRange.start, { endDate: sameDayLastWeek });
          const barLabel = dateFormatter(barChartDate, { type: DateType.DAY_YEAR });
          return { cumulativeBarLabel, barLabel };
        }
        case MONTH: {
          const sameDayLastMonth = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          sameDayLastMonth.setMonth(sameDayLastMonth.getMonth() - 1);

          const lastDay = sameDayLastMonth > lastRange.end ? lastRange.end : sameDayLastMonth;
          const cumulativeBarLabel = dateFormatter(lastRange.start, { endDate: lastDay });
          const barLabel = dateFormatter(barChartDate, { type: DateType.DAY_YEAR });
          return { cumulativeBarLabel, barLabel };
        }
        case QUARTER: {
          const lastQuarterMonth = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          lastQuarterMonth.setMonth(lastQuarterMonth.getMonth() - 3);

          const cumulativeBarLabel = dateFormatter(lastRange.start, {
            endDate: lastDateOfMonth(lastQuarterMonth),
          });
          const endDate = lastDateOfMonth(barChartDate);
          const barLabel = dateFormatter(barChartDate, { endDate });
          return { cumulativeBarLabel, barLabel };
        }
        case YEAR: {
          const lastYearMonth = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          lastYearMonth.setFullYear(lastYearMonth.getFullYear() - 1);

          const cumulativeBarLabel = dateFormatter(lastRange.start, {
            endDate: lastDateOfMonth(lastYearMonth),
          });
          const endDate = lastDateOfMonth(barChartDate);
          const barLabel = dateFormatter(barChartDate, { endDate });
          return { cumulativeBarLabel, barLabel };
        }
        default:
          return emptyLabels;
      }
    case PeriodType.STLY:
      switch (period) {
        case WEEK: {
          const sameDayLastYear = utcDate(
            `${stlyRange.start.getFullYear()}-${stlyRange.start.getMonth() + 1}-${stlyRange.start.getDate()}`,
          );
          const offsetDays = (barChartDate.getDay() + 6) % 7;
          sameDayLastYear.setDate(sameDayLastYear.getDate() + offsetDays);

          const cumulativeBarLabel = dateFormatter(stlyRange.start, { endDate: sameDayLastYear });
          const barLabel = dateFormatter(barChartDate, { type: DateType.DAY_YEAR });
          return { cumulativeBarLabel, barLabel };
        }
        case MONTH: {
          const sameDayLastYear = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          sameDayLastYear.setFullYear(sameDayLastYear.getFullYear() - 1);

          const lastDay = sameDayLastYear > stlyRange.end ? stlyRange.end : sameDayLastYear;
          const cumulativeBarLabel = dateFormatter(stlyRange.start, { endDate: lastDay });
          const barLabel = dateFormatter(barChartDate, { type: DateType.DAY_YEAR });
          return { cumulativeBarLabel, barLabel };
        }
        case QUARTER: {
          const sameMonthLastYear = utcDate(
            `${barChartDate.getFullYear()}-${barChartDate.getMonth() + 1}-${barChartDate.getDate()}`,
          );
          sameMonthLastYear.setFullYear(sameMonthLastYear.getFullYear() - 1);

          const cumulativeBarLabel = dateFormatter(stlyRange.start, {
            endDate: lastDateOfMonth(sameMonthLastYear),
          });
          const endDate = lastDateOfMonth(barChartDate);
          const barLabel = dateFormatter(barChartDate, { endDate });
          return { cumulativeBarLabel, barLabel };
        }
        default:
          return emptyLabels;
      }
    default:
      return emptyLabels;
  }
};

const getEndDate = (period, barChartDate, relativeDates) => {
  if (isWeekOrMonth(period)) {
    return barChartDate >= relativeDates.yesterday ? relativeDates.yesterday : barChartDate;
  }

  if (period === QUARTER || period === YEAR) {
    const sameMonth = barChartDate.getMonth() === relativeDates.yesterday.getMonth();
    return sameMonth
      ? relativeDates.yesterday
      : barChartDate > relativeDates.yesterday
      ? relativeDates.yesterday
      : lastDateOfMonth(barChartDate);
  }

  return relativeDates.yesterday;
};

const getBarActualTooltips = (periodType, period, barChartDate, relativeDates, rangesApi, amount, dateFormatter) => {
  const { periodScope } = rangesApi;
  let tooltipDatePart;
  const tooltipAmountPart = `Actuals: ${amount}`;

  if (periodType === PeriodType.CURRENT && periodScope === PERIOD_SCOPE.CURRENT) {
    switch (period) {
      case WEEK: {
        const endDate = getEndDate(WEEK, barChartDate, relativeDates);
        tooltipDatePart = dateFormatter(relativeDates.weekStart, { endDate });
        break;
      }
      case MONTH: {
        const endDate = getEndDate(MONTH, barChartDate, relativeDates);
        tooltipDatePart = dateFormatter(relativeDates.monthStart, { endDate });
        break;
      }
      case QUARTER: {
        const endDate = getEndDate(QUARTER, barChartDate, relativeDates);
        tooltipDatePart = dateFormatter(relativeDates.quarterStart, { endDate });
        break;
      }
      case YEAR: {
        const endDate = getEndDate(YEAR, barChartDate, relativeDates);
        tooltipDatePart = dateFormatter(relativeDates.yearStart, { endDate });
        break;
      }
      default:
        tooltipDatePart = null;
    }
  }

  return tooltipDatePart ? [tooltipDatePart, tooltipAmountPart] : tooltipAmountPart;
};

const getBarForecastTooltips = (periodType, period, barChartDate, relativeDates, amount, dateFormatter) => {
  let tooltipDatePart;
  const tooltipAmountPart = `Forecast: ${amount}`;

  if (periodType === PeriodType.CURRENT) {
    switch (period) {
      case WEEK:
      case MONTH: {
        tooltipDatePart = dateFormatter(relativeToday(relativeDates), { endDate: barChartDate });
        break;
      }
      case QUARTER:
      case YEAR: {
        const endDate = lastDateOfMonth(barChartDate);
        tooltipDatePart = dateFormatter(relativeToday(relativeDates), { endDate });
        break;
      }
      default:
        tooltipDatePart = null;
    }
  }

  return tooltipDatePart ? [tooltipDatePart, tooltipAmountPart] : tooltipAmountPart;
};

const getTargetValue = (selectedPeriod, barChartDate) => {
  if (selectedPeriod === WEEK) {
    // x values are weekday names
    const dayName = WEEKDAY_NAMES[(barChartDate.getDay() + 6) % 7];
    return dayName;
  }
  if (selectedPeriod === MONTH) {
    // x values are day numbers
    return barChartDate.getDate();
  }
  if (selectedPeriod === QUARTER || selectedPeriod === YEAR) {
    // x values are month names
    return MONTH_NAMES[barChartDate.getMonth()];
  }
  return barChartDate.getDate(); // fallback
};

const getBarChartValues = (
  series,
  selectedPeriod,
  barChartDate,
  numberFormatter,
  dateFormatter,
  relativeDates,
  rangesApi,
  { cumulative } = {},
) => {
  const targetValue = getTargetValue(selectedPeriod, barChartDate);
  let current = 0;
  let forecast = 0;
  let currentTooltip = '';
  let forecastTooltip = '';
  let label = '';
  let available = true; // Whether the target value is available in the series (non cumulative)
  const { periodType } = series;

  if (!cumulative) {
    // Finds the point that exactly matches the target
    const point = series.data.find(p => p.x === targetValue);

    if (point && point.date) {
      const { barLabel } = getBarLabels(series.periodType, selectedPeriod, point.date, rangesApi, dateFormatter);
      label = barLabel;

      if (point.date > relativeDates.yesterday) {
        forecast = point.y_forecast ? point.y_forecast : point.y;
      } else {
        forecast = point.y_forecast ? point.y_forecast : 0;
        current = point.y - forecast;
      }
      forecastTooltip = `Forecast: ${numberFormatter(forecast)}`;
      currentTooltip = `Actuals: ${numberFormatter(current)}`;
    } else {
      available = false;
    }
  } else {
    // Accumulates all points up to the target, according to the period
    const { cumulativeBarLabel } = getBarLabels(
      series.periodType,
      selectedPeriod,
      barChartDate,
      rangesApi,
      dateFormatter,
    );
    label = cumulativeBarLabel;
    switch (selectedPeriod) {
      case WEEK: {
        const targetIndex = WEEKDAY_NAMES.indexOf(targetValue);
        const today = relativeToday(relativeDates);
        const forecastIndex = (today.getDay() + 6) % 7;

        series.data.forEach(point => {
          const pointIndex = WEEKDAY_NAMES.indexOf(point.x);
          if (pointIndex !== -1 && pointIndex <= targetIndex) {
            if (forecastIndex !== -1 && pointIndex >= forecastIndex) {
              if (point.date > relativeDates.yesterday) {
                forecast += point.y_forecast ? point.y_forecast : point.y;
              } else {
                current += point.y;
              }
            } else {
              current += point.y;
            }
          }
        });
        forecastTooltip = getBarForecastTooltips(
          series.periodType,
          WEEK,
          barChartDate,
          relativeDates,
          numberFormatter(forecast),
          dateFormatter,
        );
        currentTooltip = getBarActualTooltips(
          series.periodType,
          WEEK,
          barChartDate,
          relativeDates,
          rangesApi,
          numberFormatter(current),
          dateFormatter,
        );
        break;
      }
      case MONTH: {
        series.data.forEach(point => {
          if (typeof point.x === 'number' && point.x <= targetValue) {
            if (point.date > relativeDates.yesterday) {
              forecast += point.y;
            } else {
              current += point.y;
            }
          }
        });
        forecastTooltip = getBarForecastTooltips(
          series.periodType,
          MONTH,
          barChartDate,
          relativeDates,
          numberFormatter(forecast),
          dateFormatter,
        );
        currentTooltip = getBarActualTooltips(
          series.periodType,
          MONTH,
          barChartDate,
          relativeDates,
          rangesApi,
          numberFormatter(current),
          dateFormatter,
        );
        break;
      }
      case QUARTER:
      case YEAR: {
        const targetIndex = MONTH_NAMES.indexOf(targetValue);
        const yesterdayMonth = relativeDates.yesterday.getMonth();
        const forecastIndex =
          relativeDates.monthLastEnd.getMonth() === yesterdayMonth ? yesterdayMonth + 1 : yesterdayMonth;
        const dashedBar = barChartDate > relativeDates.yesterday;
        series.data.forEach(point => {
          const pointIndex = MONTH_NAMES.indexOf(point.x);
          if (pointIndex !== -1 && pointIndex <= targetIndex) {
            if (forecastIndex !== -1 && pointIndex >= forecastIndex) {
              if (point.y_forecast) {
                forecast += point.y_forecast;
                current += point.y - point.y_forecast;
              } else if (point.dashed && dashedBar) {
                forecast += point.y;
              } else {
                current += point.y;
              }
            } else {
              current += point.y;
            }
          }
        });
        forecastTooltip = getBarForecastTooltips(
          series.periodType,
          selectedPeriod,
          barChartDate,
          relativeDates,
          numberFormatter(forecast),
          dateFormatter,
        );
        currentTooltip = getBarActualTooltips(
          series.periodType,
          selectedPeriod,
          barChartDate,
          relativeDates,
          rangesApi,
          numberFormatter(current),
          dateFormatter,
        );
        break;
      }
      default:
        break;
    }
  }
  return { label, current, forecast, currentTooltip, forecastTooltip, available, periodType };
};

const usePerformanceData = (
  selectedPeriod,
  selectedMetric,
  barChartDate,
  relativeDates,
  periodScope,
  numberFormatter,
  dateFormatter,
) => {
  const {
    dailyPerformanceForecast,
    monthlyPerformanceForecast,
    dailyPerformanceHistory,
    monthlyPerformanceHistory,
  } = React.useContext(DashboardContext);

  const rangesApi = React.useMemo(() => getRanges(selectedPeriod, relativeDates, periodScope), [
    periodScope,
    relativeDates,
    selectedPeriod,
  ]);

  const lineChartData = React.useMemo(() => {
    const data = getLineGraphData(
      selectedPeriod,
      selectedMetric,
      dailyPerformanceHistory,
      dailyPerformanceForecast,
      monthlyPerformanceHistory,
      monthlyPerformanceForecast,
      rangesApi,
      relativeDates,
      dateFormatter,
    );
    return data;
  }, [
    dailyPerformanceForecast,
    dailyPerformanceHistory,
    dateFormatter,
    monthlyPerformanceForecast,
    monthlyPerformanceHistory,
    rangesApi,
    relativeDates,
    selectedMetric,
    selectedPeriod,
  ]);

  const cumulativeLineChartData = React.useMemo(() => {
    return lineChartData.map(series => {
      let cumulativeSum = 0;
      const cumulativeData = series.data.map(point => {
        cumulativeSum += point.y;
        return { ...point, y: cumulativeSum };
      });
      return { ...series, data: cumulativeData };
    });
  }, [lineChartData]);

  const barChartData = React.useMemo(() => {
    return lineChartData
      .map(series =>
        getBarChartValues(
          series,
          selectedPeriod,
          barChartDate,
          numberFormatter,
          dateFormatter,
          relativeDates,
          rangesApi,
        ),
      )
      .filter(({ available }) => available); // Filter out values with no data
  }, [lineChartData, selectedPeriod, barChartDate, numberFormatter, dateFormatter, relativeDates, rangesApi]);

  const cumulativeBarChartData = React.useMemo(() => {
    return lineChartData.map(series =>
      getBarChartValues(
        series,
        selectedPeriod,
        barChartDate,
        numberFormatter,
        dateFormatter,
        relativeDates,
        rangesApi,
        {
          cumulative: true,
        },
      ),
    );
  }, [lineChartData, selectedPeriod, barChartDate, numberFormatter, dateFormatter, relativeDates, rangesApi]);

  return {
    lineChartData,
    cumulativeLineChartData,
    barChartData,
    cumulativeBarChartData,
  };
};

export default usePerformanceData;
